import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Col, Row, Spinner } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { ApiUrl } from "../../../ApiUrl";
import "./StaffAddStudentData.css";

const buildStudentName = (student) =>
  [
    student?.student_name,
    student?.studentName,
    student?.name,
    [student?.first_name, student?.middle_name, student?.last_name]
      .filter(Boolean)
      .join(" "),
  ].find(Boolean) || "";

const parseJsonResponse = async (response) => {
  const contentType = response.headers.get("content-type") || "";
  const rawText = await response.text();

  if (!rawText.trim()) {
    return null;
  }

  if (!contentType.includes("application/json")) {
    throw new Error("Server returned an unexpected response format.");
  }

  try {
    return JSON.parse(rawText);
  } catch (error) {
    throw new Error("Server returned invalid JSON.");
  }
};

const AddStudentExamData = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [studentInfo, setStudentInfo] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadedDocuments, setUploadedDocuments] = useState([]);

  const context = useMemo(() => {
    const state = location.state || {};
    const student = state.student || {};

    return {
      student,
      studentId: student.student_id || student.studentId || "",
      classId: state.classId || student.class_id || "",
      termId: state.termId || "",
      termLabel: state.termLabel || "",
      isStaff: location.pathname.startsWith("/staff/"),
    };
  }, [location.pathname, location.state]);

  const handleClose = () => {
    navigate(context.isStaff ? "/staff/exam-result" : "/admin/result");
  };

  const fetchPageData = useCallback(async () => {
    const academicYearId = localStorage.getItem("academicSessionId");

    if (!context.studentId || !context.classId || !context.termId) {
      setError("Please select student and class from result page first.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await fetch(
        `${ApiUrl.apiurl}reportcard/subjectGroupSubjectSubsectionListBasedOnClassTerm/?academic_year_id=${academicYearId}&class_id=${context.classId}&term_id=${context.termId}&student_id=${context.studentId}`
      );

      const detailsPayload = await parseJsonResponse(response);

      if (!response.ok) {
        throw new Error(
          detailsPayload?.message || detailsPayload?.error || "Unable to load student data."
        );
      }

      const basicDetails =
        detailsPayload?.final_result_details?.basic_details?.[0] || {};
      const studentBasic =
        detailsPayload?.final_result_details?.student_basic_details?.[0] || {};

      setStudentInfo({
        termLabel:
          detailsPayload?.final_result_details?.basic_details?.[0]?.term_desc ||
          context.termLabel,
        admissionNo:
          studentBasic.admission_no || context.student.admission_no || "",
        className:
          studentBasic.class_name || context.student.class_name || "",
        sectionName:
          studentBasic.section_name || context.student.section_name || "",
        studentName:
          studentBasic.student_name || buildStudentName(context.student),
        motherName:
          studentBasic.mother_name || context.student.mother_name || "",
        sessionCode:
          studentBasic.session_code ||
          basicDetails.sesion_code ||
          localStorage.getItem("academicSession") ||
          "",
        rollNo: studentBasic.rollno || context.student.rollno || "",
        dob: studentBasic.dob || "",
        fatherName:
          studentBasic.father_name || context.student.father_name || "",
      });
    } catch (fetchError) {
      console.error("Error loading student data:", fetchError);
      setError("Unable to load student data.");
    } finally {
      setLoading(false);
    }
  }, [context.classId, context.studentId, context.termId, context.student, context.termLabel]);

  useEffect(() => {
    fetchPageData();
  }, [fetchPageData]);

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploadedFile(file);
      setError("");
      setSuccessMessage("");
    }
  };

  const handleUpload = async () => {
    if (!uploadedFile) {
      setError("Please select a file to upload.");
      return;
    }

    const academicYearId = localStorage.getItem("academicSessionId");
    const userId = sessionStorage.getItem("userId");
    const orgId = localStorage.getItem("orgId");
    const branchId = localStorage.getItem("branchId");

    setUploading(true);
    setError("");
    setSuccessMessage("");

    try {
      const formData = new FormData();
      formData.append("academic_year_id", academicYearId);
      formData.append("class_id", context.classId);
      formData.append("term_id", context.termId);
      formData.append("student_id", context.studentId);
      formData.append("document_file", uploadedFile);
      formData.append("created_by", userId);
      formData.append("org_id", orgId);
      formData.append("branch_id", branchId);

      const response = await fetch(
        `${ApiUrl.apiurl}reportcard/documentupload/`,
        {
          method: "POST",
          body: formData,
        }
      );

      const result = await parseJsonResponse(response);

      if (!response.ok) {
        throw new Error(result?.message || result?.error || "Upload failed");
      }

      setSuccessMessage(`Document uploaded successfully!`);
      setUploadedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      // Refresh documents list
      fetchUploadedDocuments();
    } catch (uploadError) {
      console.error("Error uploading document:", uploadError);
      setError(uploadError.message || "Failed to upload document.");
    } finally {
      setUploading(false);
    }
  };

  const fetchUploadedDocuments = useCallback(async () => {
    try {
      const academicYearId = localStorage.getItem("academicSessionId");
      const response = await fetch(
        `${ApiUrl.apiurl}reportcard/studentdocuments/?academic_year_id=${academicYearId}&student_id=${context.studentId}&term_id=${context.termId}`
      );

      const result = await parseJsonResponse(response);

      if (response.ok && result?.data) {
        setUploadedDocuments(Array.isArray(result.data) ? result.data : []);
      }
    } catch (error) {
      console.error("Error fetching uploaded documents:", error);
    }
  }, [context.studentId, context.termId]);

  useEffect(() => {
    if (context.studentId && context.termId) {
      fetchUploadedDocuments();
    }
  }, [context.studentId, context.termId, fetchUploadedDocuments]);

  if (loading) {
    return (
      <div className="container-fluid py-4 text-center">
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="card p-0">
            <div className="card-body">
              <p
                style={{
                  marginBottom: "0px",
                  textAlign: "center",
                  fontSize: "20px",
                  fontWeight: "700",
                }}
              >
                STUDENT DOCUMENT UPLOAD
              </p>

              <div className="row mb-3 mt-3 mx-0">
                <div className="col-12">
                  <button
                    type="button"
                    className="btn btn-primary me-2"
                    style={{ width: "150px" }}
                    onClick={handleUpload}
                    disabled={uploading || !uploadedFile}
                  >
                    {uploading ? "Uploading..." : "Upload"}
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger me-2"
                    style={{ width: "150px" }}
                    onClick={handleClose}
                  >
                    Close
                  </button>
                </div>
              </div>

              {error && <div className="alert alert-danger mx-2 mt-2">{error}</div>}
              {successMessage && (
                <div className="alert alert-success mx-2 mt-2">{successMessage}</div>
              )}

              {/* Student Info Display */}
              <div className="row mt-3 mx-2">
                <div
                  className="student-info-box"
                  style={{
                    border: "1px solid #ddd",
                    padding: "20px",
                    borderRadius: "5px",
                    marginBottom: "20px",
                    backgroundColor: "#f9f9f9",
                  }}
                >
                  <Row>
                    <Col md={6}>
                      <p>
                        <strong>Academic Session:</strong> {studentInfo?.sessionCode || "-"}
                      </p>
                      <p>
                        <strong>Class:</strong> {studentInfo?.className || "-"}
                      </p>
                      <p>
                        <strong>Section:</strong> {studentInfo?.sectionName || "-"}
                      </p>
                      <p>
                        <strong>Admission No:</strong> {studentInfo?.admissionNo || "-"}
                      </p>
                    </Col>
                    <Col md={6}>
                      <p>
                        <strong>Name:</strong> {studentInfo?.studentName || "-"}
                      </p>
                      <p>
                        <strong>Date of Birth:</strong> {studentInfo?.dob || "-"}
                      </p>
                      <p>
                        <strong>Mother's Name:</strong> {studentInfo?.motherName || "-"}
                      </p>
                      <p>
                        <strong>Father/Guardian's Name:</strong> {studentInfo?.fatherName || "-"}
                      </p>
                    </Col>
                  </Row>
                </div>
              </div>

              {/* Document Upload Section */}
              <div className="row mt-4 mx-2 mb-4">
                <div className="col-12">
                  <div
                    className="upload-section"
                    style={{
                      border: "1px solid #ddd",
                      padding: "30px",
                      borderRadius: "5px",
                      backgroundColor: "#ffffff",
                    }}
                  >
                    <h5 className="mb-3">Upload Document</h5>
                    <div className="mb-3">
                      <label htmlFor="documentInput" className="form-label">
                        Select Document
                      </label>
                      <div className="input-group">
                        <input
                          type="file"
                          className="form-control"
                          id="documentInput"
                          ref={fileInputRef}
                          onChange={handleFileSelect}
                          accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                        />
                        <span className="input-group-text">
                          {uploadedFile
                            ? uploadedFile.name
                            : "No file chosen"}
                        </span>
                      </div>
                      <small className="text-muted d-block mt-2">
                        Supported formats: PDF, DOC, DOCX, XLS, XLSX, JPG, JPEG, PNG
                      </small>
                    </div>
                  </div>
                </div>
              </div>

              {/* Uploaded Documents List */}
              {uploadedDocuments.length > 0 && (
                <div className="row mx-2 mb-4">
                  <div className="col-12">
                    <h5 className="mb-3">Uploaded Documents</h5>
                    <div className="table-responsive">
                      <table className="table table-bordered table-striped">
                        <thead className="table-light">
                          <tr>
                            <th>Sr. No</th>
                            <th>Document Name</th>
                            <th>Upload Date</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {uploadedDocuments.map((doc, index) => (
                            <tr key={doc.id || index}>
                              <td>{index + 1}</td>
                              <td>{doc.file_name || doc.document_name || "-"}</td>
                              <td>
                                {doc.created_at
                                  ? new Date(doc.created_at).toLocaleDateString()
                                  : "-"}
                              </td>
                              <td>
                                {doc.file_path && (
                                  <a
                                    href={doc.file_path}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-sm btn-info"
                                  >
                                    View
                                  </a>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddStudentExamData;
